import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { logEvent } from '../utils/analytics';
import './QuestionBankRevision.css';
import './Dashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

function QuestionBankRevision() {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [activeMenu, setActiveMenu] = useState('question-bank');
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isAnswerSubmitted, setIsAnswerSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(true);
  const [explanation, setExplanation] = useState('');
  const [isSubmittingAnswer, setIsSubmittingAnswer] = useState(false);

  useEffect(() => {
    checkSession();
  }, []);

  useEffect(() => {
    if (userData) {
      loadRevisionQuestions();
    }
  }, [userData]);

  useEffect(() => {
    if (questions.length > 0 && currentQuestionIndex < questions.length) {
      setCurrentQuestion(questions[currentQuestionIndex]);
      setIsAnswerSubmitted(false);
      setSelectedOption(null);
      setIsCorrect(false);
      setExplanation('');
    }
  }, [currentQuestionIndex, questions]);

  const checkSession = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/doctor-auth/check-session`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.authenticated && data.data && data.data.doctor) {
        setUserData(data.data.doctor);
      } else {
        navigate('/login');
      }
    } catch (error) {
      console.error('Error checking session:', error);
      navigate('/login');
    }
  };

  const loadRevisionQuestions = async () => {
    setIsLoadingQuestions(true);
    try {
      const response = await fetch(`${API_BASE_URL}/question-bank/practice/revision-due`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.questions) {
        setQuestions(data.questions);
        setCurrentQuestionIndex(0);
      }
    } catch (error) {
      console.error('Error loading revision questions:', error);
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  const handleSubmitAnswer = async () => {
    if (!currentQuestion || !selectedOption) {
      alert('Please select an answer');
      return;
    }

    const isAnswerCorrect = selectedOption.toUpperCase() === currentQuestion.correct_answer?.toUpperCase();
    setIsCorrect(isAnswerCorrect);
    setIsAnswerSubmitted(true);
    setIsSubmittingAnswer(true);

    try {
      // Submit answer to get explanation
      const submitResponse = await fetch(`${API_BASE_URL}/question-bank/practice/attempt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          question_id: currentQuestion.question_id,
          selected_answer: selectedOption,
          time_taken: null
        })
      });
      const submitData = await submitResponse.json();
      if (submitData.explanation) {
        setExplanation(submitData.explanation);
      }

      // Mark as revised
      await fetch(`${API_BASE_URL}/question-bank/practice/mark-revised`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          question_id: currentQuestion.question_id,
          is_correct: isAnswerCorrect
        })
      });

      logEvent('Question Bank', 'revision_completed', {
        question_id: currentQuestion.question_id,
        is_correct: isAnswerCorrect,
        review_count: currentQuestion.review_count
      });
    } catch (error) {
      console.error('Error marking as revised:', error);
    } finally {
      setIsSubmittingAnswer(false);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // Completed all revision questions
      alert('Great job! You have completed all revision questions for today!');
      navigate('/question-bank');
    }
  };

  const handleMenuClick = (menuItem) => {
    setActiveMenu(menuItem);
    if (menuItem === 'home') {
      navigate('/dashboard');
    } else if (menuItem === 'ai-chat') {
      navigate('/chat');
    } else if (menuItem === 'exams') {
      navigate('/exams');
    } else if (menuItem === 'question-bank') {
      navigate('/question-bank');
    } else if (menuItem === 'feedback') {
      navigate('/feedback');
    } else if (menuItem === 'profile') {
      navigate('/edit-profile');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE_URL}/doctor-auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  const getMenuItems = () => {
    if (!userData) return [];

    if (userData.user_type === 'medical_student') {
      return [
        { id: 'home', label: 'Home', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'ai-chat', label: 'AI Chat', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'exams', label: 'Exams', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 19V5C4 3.89543 4.89543 3 6 3H19.4C19.7314 3 20 3.26863 20 3.6V16.7143" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="M6 17L20 17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="M6 21L20 21" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="M6 21C4.89543 21 4 20.1046 4 19C4 17.8954 4.89543 17 6 17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 7L15 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg> },
                { id: 'question-bank', label: 'Question Bank', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2 3H8C9.06087 3 10.0783 3.42143 10.8284 4.17157C11.5786 4.92172 12 5.93913 12 7V21C12 20.2044 11.6839 19.4413 11.1213 18.8787C10.5587 18.3161 9.79565 18 9 18H2V3Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M22 3H16C14.9391 3 13.9217 3.42143 13.1716 4.17157C12.4214 4.92172 12 5.93913 12 7V21C12 20.2044 12.3161 19.4413 12.8787 18.8787C13.4413 18.3161 14.2044 18 15 18H22V3Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'profile', label: 'Profile', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> }
      ];
    } else {
      return [
        { id: 'home', label: 'Home', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'ai-chat', label: 'AI Chat', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'profile', label: 'Profile', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> }
      ];
    }
  };

  const menuItems = getMenuItems().filter(item => item.id !== 'profile');

  return (
    <div className="question-bank-revision">
      {/* Top Header */}
      <div className="dashboard-header">
        <div className="dashboard-header-left">
          <img src="/logo.svg" alt="S.C.A.L.P.E.L. Logo" className="dashboard-logo-img" />
        </div>
        <nav className="dashboard-nav desktop-nav">
          {menuItems.map((item) => (
            <button
              key={item.id}
              className={`nav-menu-item ${activeMenu === item.id ? 'active' : ''}`}
              onClick={() => handleMenuClick(item.id)}
            >
              <span className="nav-menu-icon">{item.icon}</span>
              <span className="nav-menu-label">{item.label}</span>
            </button>
          ))}
        </nav>
        <button onClick={handleLogout} className="dashboard-logout-btn">
          <svg width="20" height="20" strokeWidth="1.4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor">
            <path d="M12 12H19M19 12L16 15M19 12L16 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
            <path d="M19 6V5C19 3.89543 18.1046 3 17 3H7C5.89543 3 5 3.89543 5 5V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V18" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
          </svg>
          <span className="logout-text">Logout</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="revision-container">
        <div className="revision-header">
          <button className="back-btn" onClick={() => navigate('/question-bank')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Question Bank
          </button>
          <div className="revision-title-section">
            <h1 className="revision-title">Revision Due Today</h1>
            <p className="revision-subtitle">
              Question {currentQuestionIndex + 1} of {questions.length}
              {currentQuestion && ` • Review #${currentQuestion.review_count + 1}`}
            </p>
          </div>
        </div>

        {isLoadingQuestions ? (
          <div className="loading-state">
            <img src="/logo.svg" alt="Loading" className="loading-logo" />
            <p>Loading revision questions...</p>
          </div>
        ) : !currentQuestion ? (
          <div className="empty-state">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="#10b981">
              <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <h3>All caught up!</h3>
            <p>You have no questions due for revision today. Great work!</p>
            <button className="primary-btn" onClick={() => navigate('/question-bank')}>
              Back to Question Bank
            </button>
          </div>
        ) : (
          <div className="question-card">
            {/* Review Info Badge */}
            <div className="review-badge">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              </svg>
              Review #{currentQuestion.review_count + 1}
            </div>

            {/* Question Text */}
            <div className="question-content">
              <h3 className="question-text">{currentQuestion.question_text}</h3>

              {/* MCQ Options */}
              <div className="question-options">
                {['option_a', 'option_b', 'option_c', 'option_d', 'option_e'].map((optKey) => {
                  const optValue = currentQuestion[optKey];
                  if (!optValue) return null;

                  const optionLetter = optKey.split('_')[1].toUpperCase();
                  const isCorrectAnswer = optionLetter === currentQuestion.correct_answer?.toUpperCase();

                  return (
                    <label
                      key={optKey}
                      className={`question-option ${selectedOption === optionLetter ? 'selected' : ''} ${isAnswerSubmitted && isCorrectAnswer ? 'correct' : ''} ${isAnswerSubmitted && selectedOption === optionLetter && !isCorrectAnswer ? 'incorrect' : ''}`}
                    >
                      <input
                        type="radio"
                        name="question-option"
                        value={optionLetter}
                        checked={selectedOption === optionLetter}
                        onChange={(e) => setSelectedOption(e.target.value)}
                        disabled={isAnswerSubmitted}
                        className="question-option-input"
                      />
                      <span className="question-option-label">
                        <strong>{optionLetter}.</strong> {optValue}
                      </span>
                      {isAnswerSubmitted && isCorrectAnswer && (
                        <svg className="option-check-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                      {isAnswerSubmitted && selectedOption === optionLetter && !isCorrectAnswer && (
                        <svg className="option-cross-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Action Buttons */}
            {!isAnswerSubmitted && (
              <div className="question-actions">
                <button
                  className="submit-btn"
                  onClick={handleSubmitAnswer}
                  disabled={!selectedOption || isSubmittingAnswer}
                >
                  {isSubmittingAnswer ? 'Generating explanation...' : 'Submit Answer'}
                </button>
              </div>
            )}

            {/* Result Section */}
            {isAnswerSubmitted && (
              <div className="question-result">
                <div className={`result-badge ${isCorrect ? 'correct' : 'incorrect'}`}>
                  {isCorrect ? (
                    <>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Correct! Well done!</span>
                    </>
                  ) : (
                    <>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      <span>Keep practicing!</span>
                    </>
                  )}
                </div>

                {explanation && (
                  <div className="question-explanation">
                    <h4 className="explanation-title">Explanation</h4>
                    <p className="explanation-text">{explanation}</p>
                  </div>
                )}

                <div className="result-actions">
                  <button className="next-btn" onClick={handleNextQuestion}>
                    {currentQuestionIndex < questions.length - 1 ? 'Next Question' : 'Finish Revision'}
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile Bottom Menu */}
      <nav className="dashboard-nav mobile-nav">
        {menuItems.map((item) => (
          <button
            key={item.id}
            className={`nav-menu-item ${activeMenu === item.id ? 'active' : ''}`}
            onClick={() => handleMenuClick(item.id)}
          >
            <span className="nav-menu-icon">{item.icon}</span>
            <span className="nav-menu-label">{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

export default QuestionBankRevision;
